<script>
import store from '@state/store'
import { convertArrayToCSV } from 'convert-array-to-csv'
import { authComputed } from '@state/helpers'
import InitDateRange from '@reports/mixins/initDateRange.vue'
import DateRange from './dateRange.vue'

export default {
  components: {
    DateRange,
  },
  mixins: [InitDateRange],
  data() {
    return {
      groupName: '',
      groups: [],
      reports: {},
    }
  },
  computed: {
    ...authComputed,
    companyId() {
      return +this.$route.params.companyId
    },
  },
  mounted() {
    this.initLastTwoYearsData()
  },
  methods: {
    convertToCSV() {
      const reports = this.reports
      const header = ['GroupName/Time', ...this.reportsLabel]

      const emptyArr = new Array(5)
      const dataSettings = this.dataSettings
      const rows = this.groups.flatMap((group) => [
        ...dataSettings.map((item, j) => {
          const groupData = reports[group.groupName]
          return [
            `${group.groupName}/${item.desc}`,
            groupData.volunteerHours[j],
            groupData.actsOfKyndness[j],
            groupData.gratitude[j],
            groupData.donationDollars[j],
          ]
        }),
        emptyArr,
      ])

      return convertArrayToCSV(rows, { header })
    },
    exportDataPoints() {
      const csvString = this.convertToCSV()
      this.exportDataFunc(csvString, 'groupsReports.csv')
    },
    fetchData(dateRange) {
      const params = {
        companyId: this.companyId,
        from: dateRange.from,
        to: dateRange.to,
      }

      store
        .dispatch('reports/fetchGroupsReport', params)
        .then((result) => {
          this.dataSettings.push(dateRange)

          result.forEach((item) => {
            if (this.groups.length < result.length) {
              this.groups.push({
                name: item.groupName,
                value: item.groupName,
                ...item,
              })
            }
            if (
              this.reports[item.groupName] &&
              this.reports[item.groupName].volunteerHours
            ) {
              this.reports[item.groupName].volunteerHours.push(
                item.volunteerHours
              )
              this.reports[item.groupName].actsOfKyndness.push(
                item.actsOfKyndness
              )
              this.reports[item.groupName].gratitude.push(item.gratitude)
              this.reports[item.groupName].donationDollars.push(
                item.donationDollars
              )
            } else {
              const obj = {
                groupName: item.groupName,
                volunteerHours: [item.volunteerHours],
                actsOfKyndness: [item.actsOfKyndness],
                gratitude: [item.gratitude],
                donationDollars: [item.donationDollars],
              }
              this.reports[item.groupName] = obj
            }
          })
          this.groupName = this.groups[0].value
        })
        .catch((err) => {
          alert(err.message || 'Something went wrong.')
        })
    },
  },
}
</script>

<template>
  <div>
    <!-- date range -->
    <div class="flex justify-between border-b border-gray pb-8 mb-8">
      <DateRange
        @add-data-points="addDataPoints"
        @export-data-points="exportDataPoints"
      />

      <ul>
        <li
          v-for="report in dataSettings"
          :key="report.colorClass"
          class="flex items-center mb-1"
        >
          <span
            :class="report.colorClass"
            class="inline-block w-6 h-6 mr-3"
          ></span>
          <span class="font-bold text-basel">{{ report.desc }}</span>
        </li>
      </ul>
    </div>

    <!-- Group -->
    <div v-if="dataSettings.length" class="border-b border-gray px-4 pb-8 mb-8">
      <div class="w-1/3 mb-6">
        <BaseSelectBox
          id="groupsOption"
          v-model="groupName"
          :options="groups"
          label="Group:"
        />
      </div>

      <!-- title -->
      <ul class="flex font-bold text-basel mb-3">
        <li
          v-for="reportlabel in reportsLabel"
          :key="reportlabel"
          class="w-1/4"
        >
          {{ reportlabel }}
        </li>
      </ul>
      <!-- data points -->
      <ul class="flex mb-2">
        <li class="w-1/4">
          <div
            v-for="(hour, i) in reports[groupName].volunteerHours"
            :key="i"
            class="flex items-center"
          >
            <span class="mr-4">{{ hour }}</span>
            <div
              :class="dataSettings[i].colorClass"
              class="h-8"
              :style="{
                width: `${getProgressbar(
                  hour,
                  reports[groupName].volunteerHours,
                )}px`,
              }"
            />
          </div>
        </li>
        <li class="w-1/4">
          <div
            v-for="(acts, i) in reports[groupName].actsOfKyndness"
            :key="i"
            class="flex items-center"
          >
            <span class="mr-4">{{ acts }}</span>
            <div
              :class="dataSettings[i].colorClass"
              class="h-8"
              :style="{
                width: `${getProgressbar(
                  acts,
                  reports[groupName].actsOfKyndness,
                )}px`,
              }"
            />
          </div>
        </li>
        <li class="w-1/4">
          <div
            v-for="(gratitude, i) in reports[groupName].gratitude"
            :key="i"
            class="flex items-center"
          >
            <span class="mr-4">{{ gratitude }}</span>
            <div
              :class="dataSettings[i].colorClass"
              class="h-8"
              :style="{
                width: `${getProgressbar(
                  gratitude,
                  reports[groupName].gratitude,
                )}px`,
              }"
            />
          </div>
        </li>
        <li class="w-1/4">
          <div
            v-for="(donation, i) in reports[groupName].donationDollars"
            :key="i"
            class="flex items-center"
          >
            <span class="mr-4">{{ donation }}</span>
            <div
              :class="dataSettings[i].colorClass"
              class="h-8"
              :style="{
                width: `${getProgressbar(
                  donation,
                  reports[groupName].donationDollars,
                )}px`,
              }"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- sorted groups -->
    <div class="px-4">
      <ul class="flex mb-4">
        <li
          v-for="reportlabel in reportsLabel"
          :key="reportlabel"
          class="w-1/4 font-bold text-basel"
        >
          {{ reportlabel + ' by Group' }}
        </li>
      </ul>
      <div class="flex">
        <ol class="w-1/4 pl-4">
          <li
            v-for="group in sortingList(groups, 'volunteerHours')"
            :key="group.value"
            class="mb-2 list-decimal"
          >
            <span class="text-blue-darker">
              [{{ group.value }}] {{ group.volunteerHours }}
            </span>
          </li>
        </ol>
        <ol class="w-1/4 pl-4">
          <li
            v-for="group in sortingList(groups, 'actsOfKyndness')"
            :key="group.value"
            class="mb-2 list-decimal"
          >
            <span class="text-blue-darker">
              [{{ group.value }}] {{ group.actsOfKyndness }}
            </span>
          </li>
        </ol>
        <ol class="w-1/4 pl-4">
          <li
            v-for="group in sortingList(groups, 'gratitude')"
            :key="group.value"
            class="mb-2 list-decimal"
          >
            <span class="text-blue-darker">
              [{{ group.value }}] {{ group.gratitude }}
            </span>
          </li>
        </ol>
        <ol class="w-1/4 pl-4">
          <li
            v-for="group in sortingList(groups, 'donationDollars')"
            :key="group.value"
            class="mb-2 list-decimal"
          >
            <span class="text-blue-darker">
              [{{ group.value }}] {{ group.donationDollars }}
            </span>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>
